import {
  isRubyOffer,
  isWebcoinOffer,
  isCashPack,
} from '@wwe-portal/web/utils/offer';
import { getDefaultMeta, sendGameTransactionEvent, sendSysUI } from './common';
import { addDDError } from './datadog';

import { OfferData } from '@wwe-portal/entities/offer.type';
import { getCurrencyRates, getItemQuantityByText } from './offer';

export const sendRedeemCouponEventError = ({ userId }) => {
  try {
    sendSysUI({
      sys_user_id: userId,
      flow_name: 'web_promo_code',
      screen_name: 'promo_code_invalid',
      screen_type: 'pop_up',
      screen_subcategory: 'promo_code',
      screen_content: '',
      interaction_type: 'load',
      interaction_object: null,
      interaction_object_category: null,
      flow_order: null,
    });
  } catch (e) {
    addDDError(e);
  }
};

export const sendRedeemCouponEventSuccess = ({
  offerDataList,
  promotionId,
  inputCode,
  ruby,
  webCoin,
  currentUser,
  currencies,
}: {
  offerDataList: OfferData[];
  promotionId: string;
  inputCode: string;
  ruby: number;
  webCoin: number;
  currentUser: { userId: string | null };
  currencies: {
    name: string;
    rate: number;
  }[];
}) => {
  try {
    const transactionId = crypto.randomUUID();
    const { webCoinCurrencyRate, rubyRate } = getCurrencyRates(currencies);
    sendSysUI({
      flow_name: 'web_promo_code',
      screen_name: 'promo_code_success',
      screen_type: 'pop_up',
      screen_category: 'special_offers',
      screen_subcategory: 'promo_code',
      interaction_type: 'load',
      interaction_object: null,
      interaction_object_category: null,
      screen_content:
        JSON.stringify({
          transactionId,
          offers: offerDataList.map((offer) => ({
            promo_id: promotionId,
            promo_code: inputCode,
            offer_id: offer.OffersID,
          })),
        }) || '',
      flow_order: null,
    });

    const defaultSysParameter = getDefaultMeta();

    offerDataList.forEach((offerData) => {
      const initialParameters = {
        ...defaultSysParameter,
        trans_desc: 'reward_web_portal',
        trans_type: 'redeem_promo_code',
        context: offerData.OffersID ?? null,
        transaction_id: transactionId,
        event_source: null,
        transaction_type: null,
        reference_subtype_3: null,
        currentUser,
        discount_info: JSON.stringify({
          promo_id: promotionId,
          promo_code: inputCode,
        }),
        sys_user_id: currentUser?.userId ?? null,
      };
      const totalItemQuantity = offerData.analyticsItems?.reduce(
        (sum: number, item) => sum + Number(item.quantity ?? 0),
        0
      );

      if (isWebcoinOffer(offerData)) {
        sendGameTransactionEvent({
          ...initialParameters,
          item_name: 'web_coin',
          item_id: 'web_currency_web_coin',
          item_balance: webCoin,
          quantity: totalItemQuantity ?? null,
        });
        return;
      } else if (webCoinCurrencyRate) {
        // earned webcoin transaction
        sendGameTransactionEvent({
          ...initialParameters,
          item_name: 'web_coin',
          item_id: 'web_currency_web_coin',
          item_balance: webCoin,
          quantity: Math.round(
            +(offerData?.Price?.USD ?? 0) * (webCoinCurrencyRate ?? 0)
          ),
        });
      }

      if (isRubyOffer(offerData)) {
        sendGameTransactionEvent({
          ...initialParameters,
          item_name: 'ruby',
          item_id: 'web_currency_ruby',
          item_balance: ruby,
          quantity: totalItemQuantity ?? null,
        });
      } else if (rubyRate) {
        // earned ruby transaction
        sendGameTransactionEvent({
          ...initialParameters,
          item_name: 'ruby',
          item_id: 'web_currency_ruby',
          item_balance: ruby,
          quantity: Math.round(+(offerData?.Price?.USD ?? 0) * (rubyRate ?? 0)),
        });
      }

      if (isCashPack(offerData)) {
        sendGameTransactionEvent({
          ...initialParameters,
          item_id: 'HardCurrency',
          item_name: 'Cash',
          item_balance: null,
          quantity: getItemQuantityByText(offerData),
        });
      }

      offerData.analyticsItems?.forEach((item) => {
        sendGameTransactionEvent({
          ...initialParameters,
          item_id: item.itemID ?? '',
          item_name: item.itemName ?? '',
          item_balance: null,
          quantity: item.quantity == null ? null : Number(item.quantity),
        });
      });
    });
  } catch (e) {
    addDDError(e);
  }
};
